import React, { useState } from 'react';
import { Alert, Button, useToast } from '../../components/shared';
import adminAPI, { errorMessage } from '../../services/adminApi';
import { clearAgentPhotos } from '../../services/agentPhotos';
import { agentPhotosView } from './agentPhotosModel';

export function AgentPhotosCard({ settings, canManage, returnMessage, onChange }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const view = agentPhotosView(settings);

  const run = async (action, done) => {
    setBusy(true);
    try {
      const result = await action();
      if (done) done(result);
    } catch (err) {
      toast(errorMessage(err), 'alert');
    } finally {
      setBusy(false);
    }
  };

  const save = (enabled, message) =>
    run(
      () => adminAPI.setAgentPhotos(enabled),
      (next) => {
        clearAgentPhotos();
        onChange(next);
        if (message) toast(message(next));
      }
    );

  const turnOn = () => save(true, (next) => (next.state === 'on' ? 'Agent photos are on. Photos appear after the first refresh.' : 'A Microsoft 365 admin needs to approve access to profile photos.'));
  const check = () => save(true, (next) => (next.state === 'on' ? 'Approved. Agent photos are on.' : "Microsoft hasn't confirmed the approval yet."));
  const turnOff = () => save(false, () => 'Agent photos are off. Stored photos were deleted.');
  const approve = () => run(() => adminAPI.getAgentPhotosConsentLink(), (link) => window.location.assign(link.consentUrl));
  const copy = () =>
    run(
      async () => {
        const link = await adminAPI.getAgentPhotosConsentLink();
        await navigator.clipboard.writeText(link.consentUrl);
      },
      () => toast('Link copied. It works for 60 minutes.', 'link')
    );

  const buttons = {
    turnOn: (
      <Button key="turnOn" variant="primary" size="sm" onClick={turnOn} disabled={busy || !canManage}>
        Turn on
      </Button>
    ),
    approve: (
      <Button key="approve" variant="primary" size="sm" icon="external" onClick={approve} disabled={busy || !canManage}>
        Approve in Microsoft 365
      </Button>
    ),
    copy: (
      <Button key="copy" size="sm" icon="copy" onClick={copy} disabled={busy || !canManage}>
        Copy link for your Microsoft 365 admin
      </Button>
    ),
    check: (
      <Button key="check" size="sm" onClick={check} disabled={busy || !canManage}>
        Check again
      </Button>
    ),
    turnOff: (
      <Button key="turnOff" size="sm" onClick={turnOff} disabled={busy || !canManage}>
        Turn off
      </Button>
    ),
  };

  return (
    <section className="card" aria-labelledby="agent-photos-title">
      <div className="card-head">
        <div>
          <div className="t" id="agent-photos-title">
            Agent photos
          </div>
          <div className="d">Profile photos from Microsoft 365</div>
        </div>
        {view.badge && (
          <div className="acts">
            <span className={['badge', view.tone].filter(Boolean).join(' ')}>{view.badge}</span>
          </div>
        )}
      </div>
      <div className="card-body stack" style={{ gap: 12 }}>
        {returnMessage && <Alert tone={returnMessage.tone}>{returnMessage.text}</Alert>}
        <p className="small" style={{ margin: 0 }}>
          {view.body}
        </p>
        {view.meta && <p className="caption" style={{ margin: 0 }}>{view.meta}</p>}
        {view.actions.length > 0 && (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {view.actions.map((action) => buttons[action])}
          </div>
        )}
      </div>
    </section>
  );
}
